/**
 * IndexedDB storage service with Effect
 * Provides batched transactions, migrations, and schema upgrades
 */

import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import { pipe } from 'effect/Function';
import { openDB } from 'idb';
import type { IDBPTransaction } from 'idb';
import type { SyncMetadata as SyncMetadataType } from '../sync/sync-state';
import { RepositoryEvents, RepositoryEventsLive, Events } from '../events/repository-events';
import * as StoreOps from './storage-store-operations';

// Re-export for tests
export type { SyncMetadata } from '../sync/sync-state';
export type SyncMetadataInternal = SyncMetadataType;

// Storage errors
export class StorageError extends Error {
  readonly _tag = 'StorageError';
  constructor(message: string, readonly cause?: unknown) {
    super(message);
  }
}

export class NotFoundError extends Error {
  readonly _tag = 'NotFoundError';
  constructor(readonly entity: string, readonly id: string) {
    super(`${entity} with id ${id} not found`);
  }
}

// Transaction context - when present, operations use this transaction instead of creating their own
export interface IDBTransactionContext {
  readonly tx: IDBPTransaction<ExpenseDB, ('transactions' | 'groups' | 'categories' | 'syncMetadata')[], 'readwrite' | 'readonly'>;
}

export const IDBTransactionContext = Context.GenericTag<IDBTransactionContext | null>('IDBTransactionContext');

// Database schema
export interface ExpenseDB {
  transactions: {
    key: string;
    value: TransactionRecord;
    indexes: {
      'by-group': string;
      'by-sync': boolean;
      'by-created': string;
      'by-type': string;
      'by-generation': number;
    };
  };
  groups: {
    key: string;
    value: GroupRecord;
    indexes: {
      'by-sync': boolean;
    };
  };
  categories: {
    key: string;
    value: CategoryRecord;
    indexes: {
      'by-group': string;
      'by-sync': boolean;
    };
  };
  users: {
    key: string;
    value: UserRecord;
  };
  groupMembers: {
    key: [string, string]; // [group_id, user_id]
    value: GroupMemberRecord;
    indexes: {
      'by-group': string;
      'by-user': string;
    };
  };
  syncMetadata: {
    key: string; // groupId
    value: SyncMetadataInternal;
  };
  settings: {
    key: string;
    value: unknown;
  };
}

// Embedded types for enriched model
export interface TransactionUser {
  id: string;
  name: string;
  email?: string;
  avatar_url?: string;
}

export interface TransactionCategory {
  id: string;
  name: string;
  emoji?: string;
  pinned?: boolean;
}

export interface TransactionWeight {
  user_id: string;
  name: string;
  weight: number;
}

// Record types
export interface TransactionRecord {
  id: string;
  type: 'expense' | 'settlement' | 'pool_event';
  group_id: string;
  date?: string;
  created_at: string;
  updated_at?: string;
  is_synced: boolean;
  local_changes: boolean;
  sync_generation?: number;
  deleted?: boolean;
  sync_error?: string;
  // True for a record created locally that has never been created on the
  // server yet. Drives the create-vs-update decision in the sync push so that
  // expenses can use a stable client-generated UUID (idempotent create)
  // instead of a throwaway `local-` id.
  pending_create?: boolean;

  // Expense-specific fields
  category_id?: string;
  description?: string;
  amount?: number;
  currency_code?: string;
  paid_by?: string;
  created_by?: string;
  recurring_expense_id?: string | null;
  occurrence_date?: string | null;
  // Explicit user-chosen split weights, pushed to the server on create/update.
  // Only set for a deliberate custom split — absent means the backend derives
  // splits (category defaults / equal). Distinct from `weights`, which is
  // display enrichment that may simply mirror server-derived splits.
  splits?: Array<{ user_id: string; weight: number }>;

  // Settlement-specific fields
  from_user_id?: string;
  to_user_id?: string;
  kind?: 'manual' | 'pool_insert_adjustment' | 'pool_expense_adjustment';
  origin_event_id?: string | null;

  // Pool event-specific fields
  event_type?: 'insert' | 'funded_expense';
  pool_id?: string;
  pool_name?: string;
  expense_id?: string | null;

  // Enriched fields (from server or local cache)
  // These allow offline rendering without extra lookups
  user?: TransactionUser; // The user who paid (for expenses) or from_user (for settlements)
  category?: TransactionCategory; // Category details (for expenses)
  weights?: TransactionWeight[]; // Split weights and member names (for expenses)
  to_user?: TransactionUser; // Recipient user (for settlements)
  from_user?: TransactionUser; // Sender user (for settlements)
}

export interface GroupRecord {
  id: string;
  name: string;
  description?: string;
  currency_code: string;
  owner_id: string;
  created_at: string;
  updated_at?: string;
  is_synced: boolean;
  local_changes: boolean;
}

export interface CategoryRecord {
  id: string;
  group_id: string;
  name: string;
  emoji?: string;
  pinned?: boolean;
  created_at: string;
  updated_at?: string;
  is_synced: boolean;
  local_changes: boolean;
}

export interface UserRecord {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  created_at: string;
  updated_at?: string;
}

export interface GroupMemberRecord {
  group_id: string;
  user_id: string;
  role: 'owner' | 'member';
  joined_at: string;
  is_synced: boolean;
}

// Storage service interface
export interface IStorageService {
  // Transaction operations
  readonly getTransaction: (id: string) => Effect.Effect<TransactionRecord, NotFoundError | StorageError>;
  readonly getTransactionsByGroup: (groupId: string) => Effect.Effect<readonly TransactionRecord[], StorageError>;
  readonly getUnsyncedTransactions: () => Effect.Effect<readonly TransactionRecord[], StorageError>;
  readonly putTransaction: (transaction: TransactionRecord) => Effect.Effect<void, StorageError>;
  readonly putTransactions: (transactions: readonly TransactionRecord[]) => Effect.Effect<void, StorageError>;
  readonly deleteTransaction: (id: string) => Effect.Effect<void, StorageError>;

  // Group operations
  readonly getGroup: (id: string) => Effect.Effect<GroupRecord, NotFoundError | StorageError>;
  readonly getAllGroups: () => Effect.Effect<readonly GroupRecord[], StorageError>;
  readonly putGroup: (group: GroupRecord) => Effect.Effect<void, StorageError>;

  // Category operations
  readonly getCategoriesByGroup: (groupId: string) => Effect.Effect<readonly CategoryRecord[], StorageError>;
  readonly putCategory: (category: CategoryRecord) => Effect.Effect<void, StorageError>;

  // Sync metadata operations
  readonly getSyncMetadata: (groupId: string) => Effect.Effect<SyncMetadataInternal | null, StorageError>;
  readonly putSyncMetadata: (metadata: SyncMetadataInternal) => Effect.Effect<void, StorageError>;

  // Batch operations with transactions
  readonly transaction: <R, E, A>(
    stores: readonly ('transactions' | 'groups' | 'categories' | 'syncMetadata')[],
    mode: 'readonly' | 'readwrite',
    operation: Effect.Effect<A, E, R>
  ) => Effect.Effect<A, E | StorageError, R>;

  // Cleanup
  readonly close: () => Effect.Effect<void, never>;
}

export class StorageService extends Context.Tag('StorageService')<
  StorageService,
  IStorageService
>() {}

export interface StorageConfig {
  readonly dbName: string;
  readonly version: number;
}

export const StorageConfig = Context.GenericTag<StorageConfig>('StorageConfig');

export const StorageServiceLive = Layer.effect(
  StorageService,
  Effect.gen(function* () {
    const config = yield* StorageConfig;
    // Storage auto-emits repository mutation events (TransactionCreated /
    // TransactionUpdated / TransactionDeleted) so that every write is
    // observable by subscribers regardless of which code path performed it.
    const events = yield* RepositoryEvents;

    // Initialize database
    const db = yield* Effect.promise(() =>
      openDB<ExpenseDB>(config.dbName, config.version, {
        upgrade(db, oldVersion, newVersion) {
          console.log(`[Storage] Upgrading database from ${oldVersion} to ${newVersion}`);

          // Delete all old stores on upgrade
          const storeNames = ['transactions', 'groups', 'categories', 'users', 'groupMembers', 'syncMetadata', 'settings'];
          for (const storeName of storeNames) {
            if (db.objectStoreNames.contains(storeName)) {
              db.deleteObjectStore(storeName);
            }
          }

          // Create transactions store
          const txStore = db.createObjectStore('transactions', { keyPath: 'id' });
          txStore.createIndex('by-group', 'group_id');
          txStore.createIndex('by-sync', 'is_synced');
          txStore.createIndex('by-created', 'created_at');
          txStore.createIndex('by-type', 'type');
          txStore.createIndex('by-generation', 'sync_generation');

          // Create groups store
          const groupStore = db.createObjectStore('groups', { keyPath: 'id' });
          groupStore.createIndex('by-sync', 'is_synced');

          // Create categories store
          const catStore = db.createObjectStore('categories', { keyPath: 'id' });
          catStore.createIndex('by-group', 'group_id');
          catStore.createIndex('by-sync', 'is_synced');

          // Create users store
          db.createObjectStore('users', { keyPath: 'id' });

          // Create groupMembers store
          const memberStore = db.createObjectStore('groupMembers', { keyPath: ['group_id', 'user_id'] });
          memberStore.createIndex('by-group', 'group_id');
          memberStore.createIndex('by-user', 'user_id');

          // Create syncMetadata store
          db.createObjectStore('syncMetadata', { keyPath: 'groupId' });

          // Create settings store
          db.createObjectStore('settings', { keyPath: 'key' });
        },
      })
    ).pipe(
      Effect.tapError(error =>
        Effect.sync(() => console.error('[Storage] Failed to open database:', error))
      ),
      Effect.mapError(error => new StorageError('Failed to open database', error))
    );

    // Helper: Get store from context transaction or create new one
    const getStore = <S extends 'transactions' | 'groups' | 'categories' | 'syncMetadata'>(
      storeName: S,
      mode: 'readonly' | 'readwrite'
    ) =>
      Effect.gen(function* () {
        const txContext = yield* Effect.serviceOption(IDBTransactionContext);

        if (txContext._tag === 'Some' && txContext.value) {
          // Use the transaction from context
          return txContext.value.tx.objectStore(storeName);
        } else {
          // Create a new single-store transaction
          const tx = db.transaction(storeName, mode);
          // Wait for it to complete when done
          const store = tx.objectStore(storeName);
          return { store, tx };
        }
      });

    // Helper to run operation with store - handles both context and standalone tx
    const withStore = <S extends 'transactions' | 'groups' | 'categories' | 'syncMetadata', A>(
      storeName: S,
      mode: 'readonly' | 'readwrite',
      operation: (store: any) => Promise<A>
    ): Effect.Effect<A, StorageError> =>
      Effect.gen(function* () {
        const result = yield* getStore(storeName, mode);

        // If result has tx, it's a standalone transaction
        if ('tx' in result) {
          const value = yield* Effect.promise(() => operation(result.store));
          yield* Effect.promise(() => result.tx.done);
          return value;
        } else {
          // It's from context, no need to wait for tx.done
          return yield* Effect.promise(() => operation(result));
        }
      }).pipe(
        Effect.mapError(error => new StorageError(`Failed to execute ${storeName} operation`, error))
      );

    return {
      getTransaction: (id) =>
        pipe(
          withStore('transactions', 'readonly', store => StoreOps.getTransactionFromStore(store, id)),
          Effect.flatMap(result =>
            result
              ? Effect.succeed(result)
              : Effect.fail(new NotFoundError('transaction', id))
          ),
          Effect.mapError(error =>
            error instanceof NotFoundError ? error : new StorageError('Failed to get transaction', error)
          )
        ),

      getTransactionsByGroup: (groupId) =>
        pipe(
          withStore('transactions', 'readonly', store => StoreOps.getTransactionsByGroupFromStore(store, groupId)),
          Effect.map(results => results.filter(t => !t.deleted))
        ),

      getUnsyncedTransactions: () =>
        pipe(
          withStore('transactions', 'readonly', StoreOps.getAllTransactionsFromStore),
          Effect.map(results => results.filter(t => !t.is_synced))
        ),

      putTransaction: (transaction) =>
        pipe(
          withStore('transactions', 'readwrite', async (store) => {
            const existing = await StoreOps.getTransactionFromStore(store, transaction.id);
            await StoreOps.putTransactionInStore(store, transaction);
            return existing != null;
          }),
          Effect.flatMap((existed) =>
            events.publish(
              existed
                ? Events.transactionUpdated(transaction, transaction.group_id, new Date().toISOString())
                : Events.transactionCreated(transaction, transaction.group_id, new Date().toISOString())
            )
          ),
          Effect.asVoid
        ),

      putTransactions: (transactions) =>
        pipe(
          withStore('transactions', 'readwrite', async (store) => {
            return Promise.all(transactions.map(async (t) => {
              const existing = await StoreOps.getTransactionFromStore(store, t.id);
              await StoreOps.putTransactionInStore(store, t);
              return existing != null;
            }));
          }),
          Effect.flatMap((existedFlags) =>
            Effect.forEach(
              transactions,
              (t, index) =>
                events.publish(
                  existedFlags[index]
                    ? Events.transactionUpdated(t, t.group_id, new Date().toISOString())
                    : Events.transactionCreated(t, t.group_id, new Date().toISOString())
                ),
              { discard: true }
            )
          ),
          Effect.asVoid
        ),

      deleteTransaction: (id) =>
        pipe(
          withStore('transactions', 'readwrite', async (store) => {
            const existing = await StoreOps.getTransactionFromStore(store, id);
            await StoreOps.deleteTransactionFromStore(store, id);
            return existing ?? null;
          }),
          Effect.flatMap((existing) =>
            existing
              ? events.publish(Events.transactionDeleted(id, existing.group_id, new Date().toISOString()))
              : Effect.void
          ),
          Effect.asVoid
        ),

      getGroup: (id) =>
        pipe(
          withStore('groups', 'readonly', store => StoreOps.getGroupFromStore(store, id)),
          Effect.flatMap(result =>
            result
              ? Effect.succeed(result)
              : Effect.fail(new NotFoundError('group', id))
          ),
          Effect.mapError(error =>
            error instanceof NotFoundError ? error : new StorageError('Failed to get group', error)
          )
        ),

      getAllGroups: () =>
        withStore('groups', 'readonly', StoreOps.getAllGroupsFromStore),

      putGroup: (group) =>
        pipe(
          withStore('groups', 'readwrite', store => StoreOps.putGroupInStore(store, group)),
          Effect.asVoid
        ),

      getCategoriesByGroup: (groupId) =>
        withStore('categories', 'readonly', store => StoreOps.getCategoriesByGroupFromStore(store, groupId)),

      putCategory: (category) =>
        pipe(
          withStore('categories', 'readwrite', store => StoreOps.putCategoryInStore(store, category)),
          Effect.asVoid
        ),

      getSyncMetadata: (groupId) =>
        pipe(
          withStore('syncMetadata', 'readonly', store => StoreOps.getSyncMetadataFromStore(store, groupId)),
          Effect.map(result => result || null)
        ),

      putSyncMetadata: (metadata) =>
        pipe(
          withStore('syncMetadata', 'readwrite', store => StoreOps.putSyncMetadataInStore(store, metadata)),
          Effect.asVoid
        ),

      transaction: (stores, mode, operation) =>
        pipe(
          Effect.gen(function* () {
            // Open an IndexedDB transaction
            const tx = db.transaction(stores as any, mode);

            // Create transaction context
            const txContext: IDBTransactionContext = { tx: tx as any };

            // Run the operation with the transaction context provided
            // Use tapErrorCause to abort on any error
            const result = yield* pipe(
              operation,
              Effect.provideService(IDBTransactionContext, txContext),
              Effect.tapErrorCause(() =>
                Effect.sync(() => {
                  // Abort transaction on error
                  tx.abort();
                  // Suppress the unhandled rejection from tx.done
                  tx.done.catch(() => {/* Expected abort error */});
                })
              )
            );

            // Wait for transaction to complete (commit)
            // Don't wait for tx.done if transaction was aborted
            yield* Effect.promise(() => tx.done);

            return result;
          }),
          Effect.mapError(error =>
            error instanceof StorageError || error instanceof NotFoundError
              ? error
              : new StorageError('Transaction failed', error)
          )
        ),

      close: () =>
        Effect.sync(() => db.close()),
    };
  })
);

// Default configuration
export const defaultStorageConfig: StorageConfig = {
  dbName: 'ExpensesDB_v2',
  version: 1,
};

export const DefaultStorageConfigLayer = Layer.succeed(StorageConfig, defaultStorageConfig);
export const DefaultStorageLayer = Layer.provide(
  StorageServiceLive,
  Layer.merge(DefaultStorageConfigLayer, RepositoryEventsLive)
);
